<script setup>
import { ref, watch } from 'vue';
import { debounce } from 'lodash';
import UiTabbedTab from "../../containers/_uiTabbedTab.vue";

const props = defineProps({
    visible: { type: Boolean, default: false },
    currentSrc: { type: String, default: '' },
    currentAlt: { type: String, default: '' },
    mediaId: { type: [String, Number], default: null },
    mediaBankUrl: { type: String, default: '' },
});

const emit = defineEmits(['select', 'close']);

const activeTab = ref('pexels');
const selectedMediaBankEntryId = ref(null);

// Pexels state
const pexelsQuery = ref('');
const pexelsResults = ref([]);
const pexelsSearching = ref(false);
const pexelsPage = ref(1);

const normalizeMediaId = (value) => {
    if (value === null || value === undefined || value === '') {
        return null;
    }

    const parsed = Number.parseInt(value, 10);

    return Number.isNaN(parsed) ? null : parsed;
};

const searchPexels = async () => {
    if (pexelsSearching.value || pexelsQuery.value === '') return;
    pexelsSearching.value = true;

    try {
        const res = await axios.get(route('api.pexels.search'), {
            params: {
                query: pexelsQuery.value,
                per_page: 12,
                page: pexelsPage.value,
            },
        });

        if (res.data.error) {
            pexelsResults.value = [];
        } else {
            pexelsResults.value = res.data.photos || [];
        }
    } catch (e) {
        pexelsResults.value = [];
    } finally {
        pexelsSearching.value = false;
    }
};

const debouncedSearch = debounce(() => {
    pexelsPage.value = 1;
    searchPexels();
}, 600);

watch(() => pexelsQuery.value, () => {
    debouncedSearch();
});

const selectPexelsImage = (photo) => {
    emit('select', {
        src: photo.src.large || photo.src.original,
        alt: photo.alt || pexelsQuery.value,
        mediaId: null,
    });
};

watch(() => props.mediaId, (mediaId) => {
    selectedMediaBankEntryId.value = normalizeMediaId(mediaId);
});

selectedMediaBankEntryId.value = normalizeMediaId(props.mediaId);

const onChangeStatusTab = (tab) => {
    activeTab.value = tab;
};

const selectMediaBankImage = async (entryId) => {
    const normalizedId = normalizeMediaId(entryId);

    if (!normalizedId) {
        return;
    }

    selectedMediaBankEntryId.value = normalizedId;

    try {
        const { data } = await axios.get(route('mediaBank.entries.show', normalizedId));

        emit('select', {
            src: '',
            alt: data?.title || props.currentAlt || '',
            mediaId: normalizedId,
        });
    } catch (e) {
        emit('select', {
            src: '',
            alt: props.currentAlt || '',
            mediaId: normalizedId,
        });
    }
};

const pexelsNextPage = () => {
    pexelsPage.value++;
    searchPexels();
};

const pexelsPrevPage = () => {
    if (pexelsPage.value <= 1) return;
    pexelsPage.value--;
    searchPexels();
};
</script>

<template>
    <sidebar-with-slots
        v-if="visible"
        max-width-class="max-w-[400px]"
        @close="emit('close')"
    >
        <template #title>
            <h1>
                <mf-translate domainkey="page:aiPageBuilder.imagePicker.title"></mf-translate>
            </h1>
        </template>

        <template #body>
            <div class="-m-6 flex flex-col px-4">
                <ui-tabbed
                    @changestatustab="onChangeStatusTab"
                    :track-state="false"
                    :responsive="false"
                >
                    <ui-tabbed-tab
                        value="pexels"
                        title="Pexels"
                        :active="true"
                    >
                        <div>
                            <div class="relative mb-4">
                                <input-text
                                    v-model:content="pexelsQuery"
                                    :placeholder="_mft('page:aiPageBuilder.imagePicker.searchPlaceholder')"
                                    @keyup.enter="searchPexels"
                                    class="w-full"
                                />
                                <div class="absolute right-2 top-2.5">
                                    <mf-spinner-medium v-if="pexelsSearching" class="h-6 w-12" />
                                </div>
                            </div>

                            <div v-if="pexelsResults.length === 0 && !pexelsSearching" class="py-8 text-center text-gray-400">
                                <mf-translate domainkey="page:aiPageBuilder.imagePicker.searchHint"></mf-translate>
                            </div>

                            <div v-if="pexelsResults.length > 0" class="grid grid-cols-2 gap-3">
                                <div
                                    v-for="photo in pexelsResults"
                                    :key="photo.id"
                                    @click="selectPexelsImage(photo)"
                                    class="group relative cursor-pointer overflow-hidden rounded-lg"
                                >
                                    <img
                                        :src="photo.src.medium"
                                        :alt="photo.alt"
                                        class="h-32 w-full object-cover transition-opacity group-hover:opacity-75"
                                    />
                                    <div class="absolute inset-0 flex items-center justify-center opacity-0 transition-opacity group-hover:opacity-100">
                                        <span class="rounded bg-white/90 px-2 py-1 text-xs font-medium text-gray-800">
                                            <mf-translate domainkey="page:aiPageBuilder.imagePicker.select"></mf-translate>
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div v-if="pexelsResults.length > 0" class="mt-4 flex justify-between">
                                <button-base size="sm" @click="pexelsPrevPage" :disabled="pexelsPage <= 1">
                                    <mf-translate domainkey="shared:previous"></mf-translate>
                                </button-base>
                                <button-base size="sm" @click="pexelsNextPage">
                                    <mf-translate domainkey="shared:next"></mf-translate>
                                </button-base>
                            </div>
                        </div>
                    </ui-tabbed-tab>
                    <ui-tabbed-tab
                        value="media-bank"
                        :title="_mft('page:aiPageBuilder.imagePicker.mediaBank')"
                    >
                        <input-media-bank-gallery
                            type="image"
                            :content="selectedMediaBankEntryId"
                            :selected-media-bank-entry-id="selectedMediaBankEntryId"
                            @update:content="selectMediaBankImage"
                        />
                    </ui-tabbed-tab>
                </ui-tabbed>
            </div>
        </template>
    </sidebar-with-slots>
</template>
